import Icon from '@/components/ui/AppIcon';

interface BalanceData {
  category: string;
  beginning: number;
  activity: number;
  ending: number;
  variance: number;
}

interface DigitalTwinChartProps {
  data: BalanceData[];
  onDrillDown: (category: string) => void;
}

const DigitalTwinChart = ({ data, onDrillDown }: DigitalTwinChartProps) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getVarianceColor = (variance: number): string => {
    if (Math.abs(variance) < 1) return 'text-success';
    if (Math.abs(variance) < 5) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-md shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-secondary">
        <h2 className="font-mono text-lg font-semibold text-foreground">Digital Twin Balance Sheet</h2>
        <p className="font-sans text-sm text-text-secondary mt-1">
          Beginning → Activity → Ending Flow Analysis
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary border-b border-border">
              <th className="px-6 py-4 text-left font-mono text-sm font-semibold text-foreground">
                Account Category
              </th>
              <th className="px-6 py-4 text-right font-mono text-sm font-semibold text-foreground">
                Beginning Balance
              </th>
              <th className="px-6 py-4 text-right font-mono text-sm font-semibold text-foreground">
                Activity
              </th>
              <th className="px-6 py-4 text-right font-mono text-sm font-semibold text-foreground">
                Ending Balance
              </th>
              <th className="px-6 py-4 text-right font-mono text-sm font-semibold text-foreground">
                Variance %
              </th>
              <th className="px-6 py-4 text-center font-mono text-sm font-semibold text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className="border-b border-border hover:bg-secondary/50 transition-all duration-250"
              >
                <td className="px-6 py-4">
                  <span className="font-mono text-sm font-medium text-foreground">{row.category}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="font-data text-sm text-foreground">{formatCurrency(row.beginning)}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <span
                    className={`font-data text-sm ${
                      row.activity >= 0 ? 'text-success' : 'text-error'
                    }`}
                  >
                    {row.activity >= 0 ? '+' : ''}
                    {formatCurrency(row.activity)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="font-data text-sm font-semibold text-foreground">
                    {formatCurrency(row.ending)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className={`font-data text-sm font-medium ${getVarianceColor(row.variance)}`}>
                    {row.variance >= 0 ? '+' : ''}
                    {row.variance.toFixed(2)}%
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => onDrillDown(row.category)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent/10 text-accent border border-accent/20 rounded hover:bg-accent/20 transition-all duration-250 active:scale-98"
                  >
                    <Icon name="MagnifyingGlassIcon" size={14} />
                    <span className="font-mono text-xs font-medium">Drill Down</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DigitalTwinChart;